<script setup lang="ts">
import type { ISchoolGradeDisplayConfiguration } from '@/core/school-grades/schoolGrade.types';
import { hasOverallAssessmentRating } from 'common/utils/performance.assessment.entry.utils';
import { Components } from '@/api/generated/api';
import { useMobile } from '@/composables/useMobile';
import NativeDialog from '@/core/dialogsystem/NativeDialog.vue';
import PositionablePopover from '@/core/dialogs/PositionablePopover.vue';
import PerformanceAssessmentEntryCollectiveAssessmentEditor from './PerformanceAssessmentEntryCollectiveAssessmentEditor.vue';
import AnyIcon from '@/core/elements/AnyIcon.vue';
import CustomButton from '@/core/elements/CustomButton.vue';
import { EIcon, EPosition } from 'common/enum/misc.enum';
import { computed, nextTick, onBeforeUnmount, ref, toRefs, useTemplateRef, watch } from 'vue';
import type {
  ICollectiveAssessmentPayload,
  ICollectiveAssessmentRatingData,
} from './performanceAssessmentCollectiveAssessment.types';

type TCollectiveAssessmentEditorInstance = InstanceType<typeof PerformanceAssessmentEntryCollectiveAssessmentEditor>;

const emit = defineEmits<{
  apply: [payload: ICollectiveAssessmentPayload];
}>();

const props = withDefaults(
  defineProps<{
    gradeConfiguration: ISchoolGradeDisplayConfiguration | null;
    availableCompetenceDomains: Components.Schemas.CompetenceDomainDto[];
    competenceAssessment: Components.Schemas.CompetenceAssessmentDto | null;
    disabled?: boolean;
    apply?: (payload: ICollectiveAssessmentPayload) => Promise<void>;
    ratingData: ICollectiveAssessmentRatingData;
  }>(),
  {
    disabled: false,
    apply: undefined,
  },
);

const { availableCompetenceDomains, competenceAssessment, disabled, ratingData } = toRefs(props);
const { isMobile } = useMobile();

const DESKTOP_POPOVER_OFFSET_Y = 24;

const anchorElementRef = useTemplateRef<HTMLElement>('anchorElementRef');
const popoverRef = useTemplateRef<InstanceType<typeof PositionablePopover>>('popoverRef');
const mobileDialogRef = useTemplateRef<InstanceType<typeof NativeDialog>>('mobileDialogRef');
const defaultPanelRef = useTemplateRef<TCollectiveAssessmentEditorInstance>('defaultPanelRef');
const mobilePanelRef = useTemplateRef<TCollectiveAssessmentEditorInstance>('mobilePanelRef');

const isOpen = ref<boolean>(false);
const isSaving = ref(false);
const positionInternal = ref<EPosition>(EPosition.TOP_CENTER);
let popoverPositionFrame: number | null = null;

const anchorElement = computed<HTMLElement | null>(() => anchorElementRef.value);

const observedCompetenceIds = computed<Set<number>>(
  () => new Set(ratingData.value.competenceRatings.map((rating) => rating.competenceId)),
);

const hasPerformanceAssessmentTarget = computed(() =>
  hasOverallAssessmentRating(ratingData.value.performanceAssessment),
);

const hasCompetenceTarget = computed<boolean>(() => {
  return !!competenceAssessment.value && observedCompetenceIds.value.size > 0;
});

const canOpen = computed<boolean>(() => {
  return !disabled.value && (hasPerformanceAssessmentTarget.value || hasCompetenceTarget.value);
});

const activePanel = computed<TCollectiveAssessmentEditorInstance | null>(() => {
  return isMobile.value ? (mobilePanelRef.value ?? null) : (defaultPanelRef.value ?? null);
});

const hidePopover = (): void => {
  popoverRef.value?.hide();
};

const hideMobileDialog = (): void => {
  if (!mobileDialogRef.value?.element?.open) {
    isOpen.value = false;
    return;
  }

  mobileDialogRef.value?.close();
  isOpen.value = false;
};

const hide = (): void => {
  if (isMobile.value) {
    hideMobileDialog();
    return;
  }

  hidePopover();
};

const schedulePopoverPositionUpdate = async (): Promise<void> => {
  if (!isOpen.value || isMobile.value) return;

  await nextTick();

  if (popoverPositionFrame !== null) {
    cancelAnimationFrame(popoverPositionFrame);
  }

  popoverPositionFrame = requestAnimationFrame(() => {
    popoverPositionFrame = null;
    popoverRef.value?.updateDialogPosition();
  });
};

const show = async (): Promise<void> => {
  if (!canOpen.value) return;

  activePanel.value?.prepare();

  if (isMobile.value) {
    mobileDialogRef.value?.showModal();
    isOpen.value = true;
    return;
  }

  await popoverRef.value?.show();
  isOpen.value = true;
  await schedulePopoverPositionUpdate();
};

const togglePopover = async (): Promise<void> => {
  if (!canOpen.value) return;

  if (isOpen.value) {
    hide();
    return;
  }

  await show();
};

const handleApply = async (payload: ICollectiveAssessmentPayload): Promise<void> => {
  if (isSaving.value) return;
  isSaving.value = true;
  try {
    if (props.apply) await props.apply(payload);
    else emit('apply', payload);
    hide();
  } catch {
    // The save handler reports the error; keep the selection available for retry.
  } finally {
    isSaving.value = false;
  }
};

watch(isMobile, (_mobile, wasMobile) => {
  if (!isOpen.value) return;

  if (wasMobile) {
    hideMobileDialog();
    return;
  }

  hidePopover();
});

onBeforeUnmount(() => {
  if (popoverPositionFrame !== null) {
    cancelAnimationFrame(popoverPositionFrame);
  }
});
</script>

<template>
  <div class="shrink-0">
    <span ref="anchorElementRef" class="inline-flex">
      <CustomButton
        :disabled="!canOpen"
        theme="text"
        :class="[
          '!h-auto !gap-smd !border-0 !px-xs !py-xs !font-normal text-tint hover:text-tint-hover active:text-tint-hover focus:!outline-0 focus-visible:!outline-2 focus-visible:!outline-tint',
          'disabled:pointer-events-none disabled:text-tertiary',
        ]"
        :aria-expanded="isOpen"
        aria-haspopup="dialog"
        @click="togglePopover"
      >
        <AnyIcon :icon-name="EIcon.COLLECTIVE_ASSESSMENT" class="size-icon-button" />
        <span class="text-base underline underline-offset-[4px]">
          {{ $t('performanceAssessmentEntry.collectiveAssessment.button') }}
        </span>
      </CustomButton>
    </span>

    <PositionablePopover
      v-if="!isMobile"
      ref="popoverRef"
      :anchor-element="anchorElement"
      :offset-y="DESKTOP_POPOVER_OFFSET_Y"
      :position="EPosition.TOP_CENTER"
      :shadow="false"
      content-style="!overflow-visible !rounded-dialog"
      dialog-style="z-50 !bg-transparent !shadow-none !overflow-visible"
      flip-strategy="vertical"
      @update:position="positionInternal = $event"
      @hide="isOpen = false"
    >
      <PerformanceAssessmentEntryCollectiveAssessmentEditor
        ref="defaultPanelRef"
        :grade-configuration="gradeConfiguration"
        :available-competence-domains="availableCompetenceDomains"
        :competence-assessment="competenceAssessment"
        :disabled="disabled || isSaving"
        :rating-data="ratingData"
        :position="positionInternal"
        @apply="handleApply"
        @content-change="schedulePopoverPositionUpdate"
      />
    </PositionablePopover>

    <NativeDialog
      v-else
      ref="mobileDialogRef"
      class="fixed inset-0 m-0 h-dvh max-h-none w-screen max-w-none overflow-visible rounded-none border-0 bg-transparent p-0 outline-none"
      :close-on-backdrop-click="true"
      :close-on-cancel="false"
      @cancel="hideMobileDialog"
      @close="isOpen = false"
    >
      <PerformanceAssessmentEntryCollectiveAssessmentEditor
        ref="mobilePanelRef"
        :grade-configuration="gradeConfiguration"
        :available-competence-domains="availableCompetenceDomains"
        :competence-assessment="competenceAssessment"
        :disabled="disabled || isSaving"
        :rating-data="ratingData"
        variant="mobile"
        @apply="handleApply"
        @close="hideMobileDialog"
      />
    </NativeDialog>
  </div>
</template>
